import React, {forwardRef,
    useId,
} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {MoreLessButton} from './more-less-button';
import styles from './strip-of-pills.module.styl';
import {StripOfPillsContextProvider} from './strip-of-pills.context';
import {useStripOfPills} from './use-strip-of-pills';

import type {ComponentProps, ReactNode} from 'react';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

type NativeSpanProps = ComponentProps<'span'>;

export interface StripOfPillsProps extends NativeSpanProps {
    /**
     * Called when the user clicks the "More" or "Less" toggle. Receives an
     * object `{ name, isCollapsed }` where `name` is the `label` prop value
     * and `isCollapsed` is the new state after the click.
     */
    onStripChange?: (onStripChangeArg: {name: string, isCollapsed: boolean}) => void,
    /**
     * Custom element rendered in place of the default "More / Less" button.
     * Use when you need a custom affordance to toggle the collapsed state
     * (e.g. a link or an icon button).
     */
    renderButton?: ReactNode,
    /**
     * Controls the collapsed state externally. When provided the component
     * is controlled — pair with `onStripChange` to update the value. Omit to
     * let `<StripOfPills>` manage state internally.
     */
    isCollapsed?: boolean,
    /**
     * Short text displayed before the pills as a category label (e.g. `"Industry"`).
     * Rendered inside a `<span>` that is wired to `aria-labelledby` on the
     * root group element.
     */
    label?: string,
    /**
     * When `true` (default), a "More / Less" button appears when the number of
     * pills exceeds the display limit. Set to `false` to always show all pills
     * without any collapse affordance.
     */
    isCollapsible?: boolean,
}

/**
 * Horizontally arranged group of pills that collapses to show a limited count with
 * a "More / Less" toggle button.
 *
 * Renders up to a maximum number of pills by default; when `isCollapsible={true}`
 * (the default) and the children overflow that limit, a "More" button appears. Clicking
 * it expands all pills and switches to a "Less" button. Pass `isCollapsed` to control
 * the collapsed state externally; `onStripChange` is called whenever the state changes.
 * Provide `renderButton` to replace the default "More/Less" button with custom UI.
 *
 * Assign a unique `key` to each pill child to ensure correct keyboard focus management.
 * Supply `label` to get a visually rendered label prefix and an `aria-labelledby` binding.
 *
 * ## Accessibility
 * - `role="group"` (overridable) with `aria-labelledby` when `label` is set.
 * - Arrow keys (`ArrowLeft` / `ArrowRight`) and `Tab` navigate between pills.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-strip-of-pills--documentation}
 */

export const StripOfPills = forwardRef<HTMLSpanElement, StripOfPillsProps>(function StripOfPills(props, ref) {
    const {
        renderButton,
        onStripChange,
        label = '',
        role = 'group',
        isCollapsed,
        children,
        isCollapsible = true,
        ...nativeProps
    } = props;

    const labelId = useId();

    const {
        collapsed,
        setCollapsed,
        canCollapse,
        context,
        getParentProps,
    } = useStripOfPills({isCollapsed, isCollapsible});

    if (!children) {
        return null;
    }

    const button = renderButton || (
        <MoreLessButton
            isCollapsed={collapsed}
            onChange={_isCollapsed => {
                setCollapsed(_isCollapsed);
                onStripChange?.({name: label || '', isCollapsed: _isCollapsed});
            }}
        />
    );

    return (
        <StripOfPillsContextProvider context={context}>
            <span
                ref={ref}
                {...{
                    ...getParentProps(nativeProps),
                    ...(label ? {'aria-labelledby': labelId} : {}),
                    role,
                }}
                className={classNames('strip-of-pills', nativeProps.className)}>
                <span>
                    {label ? <span id={labelId} className={classes['strip-of-pills__label']}>{label}:</span> : null}
                    {children}
                </span>
                {canCollapse && isCollapsible ? button : null}
            </span>
        </StripOfPillsContextProvider>
    );
});

StripOfPills.displayName = 'StripOfPills';
